import { useCallback, useState } from "react";

// BRK-S038 — the state hook behind the "verify every extracted value" pattern
// (generalizing BRK-S055's carried-forward review). Tracks which document-
// prefilled fields still await review; `touch(key)` clears a field's marker the
// first time the broker edits it. Pure UI state (NN #9) — the values themselves
// live in the host form. Lives in lib (like usePageTitle) so the marker/banner
// component file stays components-only (react-refresh).
export function useExtractedReview(extractedKeys: string[] | undefined): {
  isPending: (key: string) => boolean;
  touch: (key: string) => void;
  /**
   * BRK-S120 — mark the keys a read that happened AFTER mount produced. The bond
   * flow's dropzone sits on the first step, so its keys cannot be known when
   * this initialises; without this the caller has to keep its own Set beside the
   * hook (which is exactly what NewSubmissionPage does, and why this exists).
   *
   * REPLACES rather than adds: a second document supersedes the first, and a
   * marker left over from a read the broker abandoned would point at a value the
   * current document never proposed.
   */
  seed: (keys: string[]) => void;
  pendingCount: number;
} {
  const [pending, setPending] = useState<Set<string>>(
    () => new Set(extractedKeys ?? []),
  );
  const touch = useCallback((key: string) => {
    setPending((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);
  const seed = useCallback((keys: string[]) => setPending(new Set(keys)), []);
  return {
    isPending: (key: string) => pending.has(key),
    touch,
    seed,
    pendingCount: pending.size,
  };
}
